import axios from "axios";
import { getAuth } from "firebase/auth";

// Chat service — replaces the previous Zendesk integration (FLEX-215).
// `openChat` opens the shared Assembled host page (funnel-cms /chat) in a
// new tab, then hands it a short-lived Assembled JWT via postMessage.
// `getAssembledJwt` fetches that JWT from the trusted backend.
// Never log the Firebase token argument or the returned JWT.

const HANDSHAKE_TIMEOUT_MS = 10_000;

export function openChat(): void {
  const funnelCmsUrl = import.meta.env.VITE_FUNNEL_CMS_URL;
  if (!funnelCmsUrl) {
    console.warn("[chatService] VITE_FUNNEL_CMS_URL is not configured");
    return;
  }

  const url = `${funnelCmsUrl}/chat?surface=fh_plus`;
  const targetOrigin = new URL(funnelCmsUrl).origin;

  // Sync window.open in the click handler avoids popup blockers.
  const chatWindow = window.open(url, "_blank");
  if (!chatWindow) {
    // Popup blocked — fall back to same-tab navigation. Chat opens anonymously.
    window.location.assign(url);
    return;
  }

  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    // Not signed in — chat opens anonymously in the new tab. Nothing else to do.
    return;
  }

  // Fetch JWT in parallel while the new tab is still loading.
  const jwtPromise = currentUser.getIdToken().then(getAssembledJwt);

  // Handshake: the child pings us with `assembled-host-ready` once its
  // postMessage listener is attached. Send the JWT then.
  const handleMessage = (event: MessageEvent): void => {
    if (event.source !== chatWindow) return;
    if (event.data?.type !== "assembled-host-ready") return;
    cleanup();
    jwtPromise
      .then((jwt) => {
        if (chatWindow.closed) return;
        chatWindow.postMessage({ type: "assembled-auth", jwt }, targetOrigin);
      })
      .catch(() => {
        // JWT fetch failed — chat stays anonymous in the new tab.
      });
  };

  const cleanup = (): void => {
    window.removeEventListener("message", handleMessage);
    window.clearTimeout(timeoutId);
  };

  const timeoutId = window.setTimeout(cleanup, HANDSHAKE_TIMEOUT_MS);
  window.addEventListener("message", handleMessage);
}

export async function getAssembledJwt(
  firebaseIdToken: string
): Promise<string> {
  const baseUrl = import.meta.env.VITE_LEAD_SERVICE_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_LEAD_SERVICE_BASE_URL is not configured");
  }

  try {
    const { data } = await axios.post<{ token?: string }>(
      `${baseUrl}/api/assembled/token`,
      { app: "fh-ionic" },
      {
        headers: {
          Authorization: `Bearer ${firebaseIdToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!data.token) {
      throw new Error("Assembled JWT missing from response body");
    }
    return data.token;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`getAssembledJwt failed: ${error.response.status}`);
    }
    throw error;
  }
}
